import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { OrderView } from '../../../../../services/orders/orders.service';
import { orderlineDisplayName, type OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import { OrderSummary } from '../summary/OrderSummary';
import { groupOrderlines } from '../shared/receipt.utils';
import { formatMoney, formatMoneyCompact } from '../../../../../utils/money';
import { groupOrderlineSelections, groupOrderlineExtraTotal } from '../../../../../utils/orderline-selections';
import classes from './receipt-screen.module.scss';
import { PendingApprovalBadge } from '../../orders/shared/PendingApprovalBadge';

type ReceiptScreenProps = {
  order: OrderView;
  orderlines: OrderlineView[];
  paymentlines: PaymentlineView[];
  discount: number;
  // Optional status pill next to the order name (e.g. "שולחן סגור" in table-history restore mode).
  statusTag?: string;
  pendingApprovalOrderlineIds?: ReadonlySet<number>;
  hasPendingGlobalDiscount?: boolean;
};

const getReceiptGroupKey = (line: OrderlineView): string => (
  line.productId == null ? `general_${line.id}` : `${line.productId}_${line.unitPrice}`
);

const ReceiptScreenComponent: React.FC<ReceiptScreenProps> = ({
  order,
  orderlines,
  paymentlines,
  discount,
  statusTag,
  pendingApprovalOrderlineIds,
  hasPendingGlobalDiscount = false,
}) => {
  const displayName = order.guestName || order.tableNumber || order.orderCode;
  // Take-away shows its unsent (pending) lines on the bill too (paid upfront).
  const isTakeAway = order.orderType === 'take_away';
  const visibleLines = React.useMemo(
    () => orderlines.filter((line) => line.status !== 'pending' || isTakeAway),
    [orderlines, isTakeAway],
  );
  // Group active and canceled separately so a canceled line never merges into an active one.
  const groupedVisibleLines = React.useMemo(
    () => groupOrderlines(visibleLines.filter((line) => line.status !== 'canceled')),
    [visibleLines],
  );
  const groupedCanceledLines = React.useMemo(
    () => groupOrderlines(visibleLines.filter((line) => line.status === 'canceled')),
    [visibleLines],
  );
  const pendingApprovalGroupKeys = React.useMemo(() => {
    const keys = new Set<string>();
    if (!pendingApprovalOrderlineIds) return keys;
    for (const line of visibleLines) {
      if (pendingApprovalOrderlineIds.has(line.id)) {
        keys.add(getReceiptGroupKey(line));
      }
    }
    return keys;
  }, [pendingApprovalOrderlineIds, visibleLines]);

  return (
    <div className={classes.receiptScreen} data-testid="receipt-screen">
      <div className={classes.orderCard}>
        <div className={classes.orderCardHeader}>
          <span className={classes.orderCardName}>
            {displayName}
            {statusTag && <span className={classes.statusTag}>{statusTag}</span>}
            {hasPendingGlobalDiscount && (
              <PendingApprovalBadge />
            )}
          </span>
          <div className={classes.orderCardIcon} aria-hidden="true">
            <FontAwesomeIcon icon={faUtensils as IconProp} />
          </div>
        </div>

        {groupedVisibleLines.length === 0 && groupedCanceledLines.length === 0 ? (
          <p className={classes.emptyItems}>אין פריטים בהזמנה.</p>
        ) : (
          <ul className={classes.itemsList}>
            {groupedVisibleLines.map((line) => {
              const hasDiscount = line.discountInput != null && line.discountInput > 0;
              const lineTotal = hasDiscount
                ? line.unitPrice * line.quantity - line.discountInput!
                : line.unitPrice * line.quantity;
              const selectionGroups = groupOrderlineSelections(line.selections);
              const hasPendingApproval = pendingApprovalGroupKeys.has(getReceiptGroupKey(line));
              return (
                <React.Fragment key={line.id}>
                  <li className={classes.itemRow} data-testid={`receipt-line-${line.id}`}>
                    <div className={classes.itemRight}>
                      <span className={classes.itemQty}>{line.quantity}</span>
                      <div className={classes.itemNameCol}>
                        <span className={classes.itemName}>
                          {orderlineDisplayName(line)}
                          {hasPendingApproval && <PendingApprovalBadge />}
                        </span>
                        {hasDiscount && (
                          <span className={classes.itemDetailRow}>
                            <span className={classes.itemDiscountBadge}>
                              הנחה ₪{formatMoneyCompact(line.discountInput!)} מתוך ₪{formatMoneyCompact(line.unitPrice * line.quantity)}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={classes.itemPrice}>{formatMoney(lineTotal)}</span>
                  </li>
                  {selectionGroups.map((group, index) => (
                    <li key={`${line.id}-sub-${index}`} className={`${classes.itemRow} ${classes.itemSubRow}`}>
                      <div className={classes.itemRight}>
                        <span className={classes.itemQty} />
                        <span className={classes.itemName}>
                          <span className={classes.selectionGroupLabel}>{group.groupName}: </span>
                          {group.options.map((opt) => opt.name).join(', ')}
                        </span>
                      </div>
                      <span className={classes.itemPrice}>+{formatMoney(groupOrderlineExtraTotal(group, line.quantity))}</span>
                    </li>
                  ))}
                </React.Fragment>
              );
            })}
            {groupedCanceledLines.map((line) => (
              <li
                key={`canceled-${line.id}`}
                className={`${classes.itemRow} ${classes.canceledRow}`}
                data-testid={`receipt-canceled-line-${line.id}`}
              >
                <div className={classes.itemRight}>
                  <span className={classes.itemQty}>{line.quantity}</span>
                  <div className={classes.itemNameCol}>
                    <span className={classes.itemName}>
                      {orderlineDisplayName(line)}
                      <span className={classes.canceledTag}>מבוטל</span>
                    </span>
                  </div>
                </div>
                <span className={classes.itemPrice}>
                  {formatMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <OrderSummary order={order} orderlines={orderlines} paymentlines={paymentlines} discount={discount} />
    </div>
  );
};

export const ReceiptScreen = React.memo(ReceiptScreenComponent);
